import { AUTHENTIC_LEADERSHIP_PACK } from "@/lib/behavior/frameworkPacks/authenticLeadershipPack";
import { ADAPTIVE_LEADERSHIP_PACK } from "@/lib/behavior/frameworkPacks/adaptiveLeadershipPack";
import { EXECUTIVE_PRESENCE_PACK } from "@/lib/behavior/frameworkPacks/executivePresencePack";
import type {
  FrameworkPackDefinition,
  FrameworkPackId,
} from "@/lib/behavior/frameworkPacks/frameworkPackTypes";
import { FRAMEWORK_PACK_IDS } from "@/lib/behavior/frameworkPacks/frameworkPackTypes";
import { INCLUSIVE_LEADERSHIP_PACK } from "@/lib/behavior/frameworkPacks/inclusiveLeadershipPack";
import { RADICAL_CANDOR_PACK } from "@/lib/behavior/frameworkPacks/radicalCandorPack";
import { SERVANT_LEADERSHIP_PACK } from "@/lib/behavior/frameworkPacks/servantLeadershipPack";

const MARSHALL_GOLDSMITH_PACK: FrameworkPackDefinition = {
  id: "marshall_goldsmith_stakeholder_centered",
  label: "Stakeholder Centered Coaching",
  family: "veera_core",
  source: "curated",
  preview: {
    philosophySummary:
      "Stakeholder Centered Coaching grounds behavior change in observable commitments, feedforward, and outside validation over time.",
    whatItNotices: [
      "Which visible leadership behaviors matter most to stakeholders right now.",
      "Whether the leader is asking for feedforward and following up with consistency.",
      "Whether improvement is being validated by other people, not only by self-report.",
    ],
    watchouts: [
      "Do not let the leader hide in insight without stakeholder follow-through.",
      "Do not overcomplicate the target behavior when one clear shift is enough.",
    ],
    sampleCoachLanguage: [
      "What would your stakeholders notice first if this behavior really improved?",
      "Where do you need more follow-up, not more reflection?",
    ],
    sampleLeaderLanguage: [
      "Your coach would likely want you to make this behavior easier for others to notice.",
      "The next rep is not just insight. It is asking for feedforward and following up on it.",
    ],
  },
  runtime: {
    coreConstructs: [
      "Stakeholder-centered change",
      "Feedforward",
      "Follow-up",
      "Visible behavior change",
    ],
    diagnosticQuestions: [
      "Which behavior should change in a way stakeholders can actually observe?",
      "Who needs to see the change, and what would they describe differently?",
      "What follow-up rhythm would make progress visible across time?",
    ],
    interventionPatterns: [
      "Narrow to one visible behavior shift.",
      "Ask for feedforward from named stakeholders.",
      "Use regular follow-up to reinforce accountability.",
    ],
    progressMarkers: [
      "Stakeholders can name the behavior shift without prompting.",
      "The leader is asking for feedforward and following up consistently.",
      "Behavior change is being validated externally, not only described internally.",
    ],
    stallSignals: [
      "The leader keeps reflecting without naming one visible behavior change.",
      "Stakeholder follow-up drops off after the initial commitment.",
    ],
  },
  operationalization: {
    noticingSignals: [
      {
        id: "stakeholder_visible_commitment",
        label: "Stakeholder-visible commitment",
        description: "A behavior commitment is named in terms other people could observe.",
        positiveIndicators: ["stakeholder", "commitment", "visible", "behavior", "notice"],
        negativeIndicators: ["private", "internal", "vague", "someday"],
        evidenceHints: ["stakeholders would notice", "visible behavior", "committed to follow up"],
        prohibitedInferences: ["approval score", "influence quality", "leader effectiveness"],
      },
      {
        id: "feedforward_follow_up_gap",
        label: "Feedforward follow-up gap",
        description: "Feedforward or follow-up is needed to make change externally visible.",
        positiveIndicators: ["feedforward", "follow-up", "ask", "check in"],
        negativeIndicators: ["dropped", "forgot", "no follow-up", "self-report"],
        evidenceHints: ["asked for feedforward", "follow-up rhythm", "external validation"],
        prohibitedInferences: ["stakeholder approval", "popularity", "political capital"],
      },
    ],
    reflectionPatterns: [
      {
        id: "visible_behavior_change",
        label: "Visible behavior change",
        promptTemplates: [
          "What would a stakeholder notice if this behavior really shifted?",
          "Who would be best positioned to offer feedforward on this moment?",
          "What follow-up would make the change visible over time?",
        ],
        useWhen: ["stakeholder_visible_commitment", "feedforward_follow_up_gap"],
        avoidWhen: ["no_named_stakeholder", "thin_evidence"],
      },
    ],
    boundaries: {
      allowed: ["visible behavior", "stakeholder expectation", "feedforward", "follow-up rhythm"],
      prohibited: ["stakeholder score", "approval rating", "rank leader effectiveness"],
      coachOnly: ["choose key stakeholders", "interpret political dynamics", "design stakeholder follow-up strategy"],
    },
    salienceWeights: {
      stakeholder_visible_commitment: 1.4,
      feedforward_follow_up_gap: 1.3,
    },
  },
};

const GROW_PACK: FrameworkPackDefinition = {
  id: "grow",
  label: "GROW",
  family: "veera_core",
  source: "curated",
  preview: {
    philosophySummary:
      "GROW structures a coaching conversation around goal, reality, options, and committed way forward so momentum turns into action.",
    whatItNotices: [
      "Whether the goal is concrete enough to guide the conversation.",
      "What current reality is being faced honestly versus avoided.",
      "Whether options are being generated before commitment is made.",
    ],
    watchouts: [
      "Do not rush from goal to advice without fully naming reality.",
      "Do not confuse a list of options with a chosen commitment.",
    ],
    sampleCoachLanguage: [
      "What is the goal for this conversation, not just the topic?",
      "What is true in the current reality that you might be skipping past?",
    ],
    sampleLeaderLanguage: [
      "Your coach would likely want you to get clearer on the goal before solving.",
      "Through this lens, the next step comes after you face reality honestly and choose one option.",
    ],
  },
  runtime: {
    coreConstructs: [
      "Goal",
      "Reality",
      "Options",
      "Way forward",
    ],
    diagnosticQuestions: [
      "What is the real goal for this leader in the next conversation or decision?",
      "What reality needs to be faced more honestly?",
      "Which option is most worth committing to now?",
    ],
    interventionPatterns: [
      "Clarify the goal in one sentence.",
      "Name the reality without softening it.",
      "Generate options before picking the next move.",
    ],
    progressMarkers: [
      "The goal is concrete enough to shape the conversation clearly.",
      "Reality is being named directly instead of being skipped or softened.",
      "One option has become a real commitment with an owner and timing.",
    ],
    stallSignals: [
      "The conversation keeps widening because the goal stays vague.",
      "Advice appears before reality and options have been fully explored.",
    ],
  },
  operationalization: {
    noticingSignals: [
      {
        id: "unclear_goal",
        label: "Unclear goal",
        description: "The conversation has a topic but not a clear goal.",
        positiveIndicators: ["goal", "topic", "outcome", "focus"],
        negativeIndicators: ["vague", "broad", "unclear", "wandering"],
        evidenceHints: ["not clear what the goal was", "topic kept widening", "no clear outcome"],
        prohibitedInferences: ["lack of discipline", "poor commitment", "unfocused leader"],
      },
      {
        id: "skipped_reality",
        label: "Skipped reality",
        description: "Advice or options appeared before the current reality was named.",
        positiveIndicators: ["reality", "options", "advice", "current state"],
        negativeIndicators: ["skipped", "premature", "jumped", "before naming"],
        evidenceHints: ["jumped to advice", "did not name reality", "options before reality"],
        prohibitedInferences: ["avoidance", "failure", "lack of honesty"],
      },
    ],
    reflectionPatterns: [
      {
        id: "goal_reality_options",
        label: "Goal and reality before options",
        promptTemplates: [
          "What was the goal in this exchange?",
          "What reality needed to be named before moving forward?",
          "Which option became a real way forward, if any?",
        ],
        useWhen: ["unclear_goal", "skipped_reality"],
        avoidWhen: ["clear_commitment_already_present"],
      },
    ],
    boundaries: {
      allowed: ["goal", "reality", "options", "way forward", "commitment clarity"],
      prohibited: ["you should commit", "lack of discipline", "failure to execute"],
      coachOnly: ["test whether stated goal is the real goal", "challenge goal-behavior misalignment", "hold accountability"],
    },
    salienceWeights: {
      unclear_goal: 1.4,
      skipped_reality: 1.5,
    },
  },
};

const IMMUNITY_TO_CHANGE_PACK: FrameworkPackDefinition = {
  id: "immunity_to_change",
  label: "Immunity to Change",
  family: "veera_core",
  source: "curated",
  preview: {
    philosophySummary:
      "Immunity to Change explains why smart leaders stay stuck by surfacing hidden commitments and assumptions that compete with stated goals.",
    whatItNotices: [
      "Where repeated patterns suggest a hidden commitment underneath the stated goal.",
      "Which self-protective assumptions are keeping the leader from experimenting.",
      "Where progress stalls even when motivation sounds sincere.",
    ],
    watchouts: [
      "Do not turn every plateau into a deep immunity diagnosis too early.",
      "Do not confuse fear with lack of commitment.",
    ],
    sampleCoachLanguage: [
      "What competing commitment might be winning here?",
      "What assumption feels so true that it keeps this pattern in place?",
    ],
    sampleLeaderLanguage: [
      "Your coach would likely want you to look at what you may be protecting, not just what you say you want.",
      "This pattern may persist because another commitment is operating underneath the surface.",
    ],
  },
  runtime: {
    coreConstructs: [
      "Improvement goal",
      "Competing commitments",
      "Big assumptions",
      "Adaptive immunity",
    ],
    diagnosticQuestions: [
      "What stated goal keeps losing out in practice?",
      "What hidden commitment might make the current pattern feel safer?",
      "What assumption would need to be tested for change to become possible?",
    ],
    interventionPatterns: [
      "Surface the competing commitment.",
      "Name the assumption behind the pattern.",
      "Design one small safe test against the assumption.",
    ],
    progressMarkers: [
      "The hidden commitment is named clearly enough to feel believable.",
      "The leader can articulate the assumption protecting the pattern.",
      "A small test is being run instead of only talking about change.",
    ],
    stallSignals: [
      "The leader keeps naming the goal but cannot surface what it competes with.",
      "Insight deepens, but no safe test of the assumption is attempted.",
    ],
  },
  operationalization: {
    noticingSignals: [
      {
        id: "repeated_stuckness",
        label: "Repeated stuckness",
        description: "The leader returns to the same pattern despite sincere intent to change.",
        positiveIndicators: ["again", "repeated", "pattern", "stuck", "intent"],
        negativeIndicators: ["collapsed", "did not sustain", "same issue", "keeps happening"],
        evidenceHints: ["same pattern again", "wanted to change", "collapsed quickly"],
        prohibitedInferences: ["fear", "hidden motive", "resistance"],
      },
      {
        id: "competing_commitment_signal",
        label: "Competing commitment signal",
        description: "The current behavior may be protecting something important enough to compete with the stated goal.",
        positiveIndicators: ["protect", "risk", "safe", "assumption", "commitment"],
        negativeIndicators: ["not yet", "too risky", "reasonable", "can't"],
        evidenceHints: ["what it protects", "competing commitment", "big assumption"],
        prohibitedInferences: ["diagnosed fear", "emotional cause", "avoidant personality"],
      },
    ],
    reflectionPatterns: [
      {
        id: "safe_assumption_testing",
        label: "Safe assumption testing",
        promptTemplates: [
          "What might the current pattern be protecting?",
          "What competing commitment could be operating alongside the stated goal?",
          "What small assumption test would be safe enough for a coach to consider?",
        ],
        useWhen: ["repeated_stuckness", "competing_commitment_signal"],
        avoidWhen: ["single_moment_only", "sensitive_emotional_claim"],
      },
    ],
    boundaries: {
      allowed: ["stated goal", "competing commitment", "big assumption", "safe test"],
      prohibited: ["diagnose fear", "hidden motive", "avoidant", "resistant"],
      coachOnly: ["name competing commitments as conclusions", "design assumption tests", "interpret protection patterns"],
    },
    salienceWeights: {
      repeated_stuckness: 1.5,
      competing_commitment_signal: 1.4,
    },
  },
};

const SOLUTION_FOCUSED_PACK: FrameworkPackDefinition = {
  id: "solution_focused",
  label: "Solution-Focused Coaching",
  family: "veera_core",
  source: "curated",
  preview: {
    philosophySummary:
      "Solution-Focused Coaching builds momentum by amplifying what already works, naming useful exceptions, and moving toward the next small shift.",
    whatItNotices: [
      "Where the leader is already generating progress or useful exceptions.",
      "What strengths or resources are available right now.",
      "Which next small move would create momentum without overengineering the problem.",
    ],
    watchouts: [
      "Do not bypass real constraints by forcing optimism.",
      "Do not overanalyze the problem once a useful next move is visible.",
    ],
    sampleCoachLanguage: [
      "Where is this already working, even a little?",
      "What is the next small move that would tell you things are shifting?",
    ],
    sampleLeaderLanguage: [
      "Your coach would likely want you to notice the exceptions, not only the breakdowns.",
      "Through this lens, the next rep is a small move that builds momentum.",
    ],
  },
  runtime: {
    coreConstructs: [
      "Exceptions",
      "Resources",
      "Desired future",
      "Next small step",
    ],
    diagnosticQuestions: [
      "Where has this gone a little better already?",
      "What strength or resource is available that the leader is underusing?",
      "What is the smallest next move that would create visible progress?",
    ],
    interventionPatterns: [
      "Find and amplify exceptions.",
      "Name the resources already present.",
      "Translate insight into one small next step.",
    ],
    progressMarkers: [
      "The leader can point to exceptions where the desired behavior already showed up.",
      "Strengths and existing resources are being named and reused.",
      "The next small move is specific and easy to repeat quickly.",
    ],
    stallSignals: [
      "The conversation loops on the problem after useful exceptions are visible.",
      "Optimism is replacing honest constraint-mapping.",
    ],
  },
  operationalization: {
    noticingSignals: [
      {
        id: "useful_exception",
        label: "Useful exception",
        description: "A moment where the desired behavior already appeared, even briefly.",
        positiveIndicators: ["worked", "better", "exception", "already", "progress"],
        negativeIndicators: ["only", "briefly", "small", "partial"],
        evidenceHints: ["went better", "worked for a moment", "exception to the pattern"],
        prohibitedInferences: ["problem solved", "constraint irrelevant", "easy fix"],
      },
      {
        id: "available_resource",
        label: "Available resource",
        description: "An existing strength, support, or resource could help the next small shift.",
        positiveIndicators: ["strength", "resource", "support", "momentum", "small step"],
        negativeIndicators: ["underused", "missed", "not reused"],
        evidenceHints: ["existing resource", "small next shift", "already available"],
        prohibitedInferences: ["force positivity", "minimize problem", "ignore constraints"],
      },
    ],
    reflectionPatterns: [
      {
        id: "exceptions_and_next_shift",
        label: "Exceptions and next small shift",
        promptTemplates: [
          "Where has this gone better, even briefly?",
          "What existing resource could support the next small shift?",
          "What small sign of progress is already available to build from?",
        ],
        useWhen: ["useful_exception", "available_resource"],
        avoidWhen: ["constraint_needs_attention_first"],
      },
    ],
    boundaries: {
      allowed: ["exception", "resource", "small next shift", "momentum", "what already works"],
      prohibited: ["problem solved", "just be positive", "ignore constraints"],
      coachOnly: ["decide whether solution focus is premature", "balance constraint realism", "challenge optimistic avoidance"],
    },
    salienceWeights: {
      useful_exception: 1.5,
      available_resource: 1.3,
    },
  },
};

const CO_ACTIVE_PACK: FrameworkPackDefinition = {
  id: "co_active",
  label: "Co-Active Coaching",
  family: "veera_core",
  source: "curated",
  preview: {
    philosophySummary:
      "Co-Active Coaching emphasizes presence, values, fulfillment, balance, and relationship so the leader is coached as a whole person, not only as a performer.",
    whatItNotices: [
      "Which values, tensions, or life forces are shaping the leader's choices.",
      "Whether the coaching stance is relational and present instead of purely diagnostic.",
      "Where balance, fulfillment, or identity needs attention alongside performance.",
    ],
    watchouts: [
      "Do not become so expansive that the coaching conversation loses traction.",
      "Do not confuse empathy with avoiding challenge.",
    ],
    sampleCoachLanguage: [
      "What value feels most alive in this moment, and which one is being crowded out?",
      "What wants more voice here besides the performing part of you?",
    ],
    sampleLeaderLanguage: [
      "Your coach would likely want you to notice the whole-person tension in this pattern, not just the performance issue.",
      "Through this lens, the next rep should honor both truth and choice.",
    ],
  },
  runtime: {
    coreConstructs: [
      "Whole person",
      "Fulfillment",
      "Balance",
      "Values and presence",
    ],
    diagnosticQuestions: [
      "What part of the leader's experience is being left out of the current frame?",
      "Which values or tensions need to be named explicitly?",
      "How can the next coaching move stay both present and challenging?",
    ],
    interventionPatterns: [
      "Name the values tension.",
      "Coach the whole person, not only the role.",
      "Use presence before reframing.",
    ],
    progressMarkers: [
      "The leader can name the underlying values tension without collapsing into performance talk.",
      "The conversation stays present while still moving toward a real choice.",
      "Behavioral decisions reflect whole-person awareness instead of role-only pressure.",
    ],
    stallSignals: [
      "Empathy is being used to avoid challenge or commitment.",
      "The conversation grows expansive without producing a grounded next move.",
    ],
  },
  operationalization: {
    noticingSignals: [
      {
        id: "values_tension",
        label: "Values tension",
        description: "A leader appears to be navigating competing values or whole-person tension.",
        positiveIndicators: ["values", "meaning", "choice", "tension", "whole person"],
        negativeIndicators: ["crowded out", "role pressure", "split", "tradeoff"],
        evidenceHints: ["value was crowded out", "whole-person tension", "role pressure"],
        prohibitedInferences: ["identity diagnosis", "emotional state", "values conflict conclusion"],
      },
      {
        id: "presence_before_reframe",
        label: "Presence before reframe",
        description: "The moment may need presence and holding before a coaching reframe or move.",
        positiveIndicators: ["presence", "energy", "hold", "listen", "choice"],
        negativeIndicators: ["rushed", "overly expansive", "fix", "avoid challenge"],
        evidenceHints: ["needed more presence", "moved too quickly to reframe", "hold space"],
        prohibitedInferences: ["empathy is enough", "leader feels", "coach should not challenge"],
      },
    ],
    reflectionPatterns: [
      {
        id: "whole_person_choice",
        label: "Whole-person choice",
        promptTemplates: [
          "What value was present here, and what may have been crowded out?",
          "What whole-person tension deserves to be named before solving?",
          "What choice point is visible without forcing closure?",
        ],
        useWhen: ["values_tension", "presence_before_reframe"],
        avoidWhen: ["needs_concrete_commitment_now"],
      },
    ],
    boundaries: {
      allowed: ["values tension", "whole person", "presence", "choice point", "fulfillment"],
      prohibited: ["infer emotional state", "replace challenge with empathy", "identity label"],
      coachOnly: ["interpret values conflicts", "decide when to challenge", "connect identity to behavior"],
    },
    salienceWeights: {
      values_tension: 1.5,
      presence_before_reframe: 1.2,
    },
  },
};

/**
 * @deprecated Use Supabase `framework_pack_catalog` table via `frameworkPackDb.ts` (edge functions)
 * or query `framework_pack_catalog` directly from the client.
 * This file is retained as a type reference and local fallback only.
 * Do not add new packs here — add them to the DB migration instead.
 */
export const FRAMEWORK_PACK_CATALOG: FrameworkPackDefinition[] = [
  MARSHALL_GOLDSMITH_PACK,
  GROW_PACK,
  IMMUNITY_TO_CHANGE_PACK,
  SOLUTION_FOCUSED_PACK,
  CO_ACTIVE_PACK,
  AUTHENTIC_LEADERSHIP_PACK,
  RADICAL_CANDOR_PACK,
  ADAPTIVE_LEADERSHIP_PACK,
  EXECUTIVE_PRESENCE_PACK,
  INCLUSIVE_LEADERSHIP_PACK,
  SERVANT_LEADERSHIP_PACK,
];

const FRAMEWORK_PACK_LOOKUP = Object.fromEntries(
  FRAMEWORK_PACK_CATALOG.map((pack) => [pack.id, pack]),
) as Record<FrameworkPackId, FrameworkPackDefinition>;

for (const id of FRAMEWORK_PACK_IDS) {
  if (!(id in FRAMEWORK_PACK_LOOKUP)) {
    throw new Error(`Missing framework pack definition for ${id}`);
  }
}

/**
 * @deprecated Use `fetchFrameworkPackById()` from `frameworkPackDb.ts` instead.
 * This local lookup is retained as a fallback for client-side code that has not
 * yet migrated to the Supabase-backed pack catalog.
 */
export function getFrameworkPackById(id: FrameworkPackId): FrameworkPackDefinition {
  return FRAMEWORK_PACK_LOOKUP[id];
}

const FRAMEWORK_PACK_TEXT_LOOKUP: Array<{ id: FrameworkPackId; tokens: string[] }> = [
  {
    id: "marshall_goldsmith_stakeholder_centered",
    tokens: [
      "marshall goldsmith stakeholder centered coaching",
      "stakeholder centered coaching",
      "marshall goldsmith",
    ],
  },
  {
    id: "grow",
    tokens: ["grow", "grow model"],
  },
  {
    id: "immunity_to_change",
    tokens: ["immunity to change"],
  },
  {
    id: "solution_focused",
    tokens: ["solution-focused coaching", "solution focused coaching", "solution-focused", "solution focused"],
  },
  {
    id: "co_active",
    tokens: ["co-active coaching", "co-active", "coactive coaching", "coactive"],
  },
  {
    id: "authentic_leadership",
    tokens: ["authentic leadership"],
  },
  {
    id: "radical_candor",
    tokens: ["radical candor", "care personally challenge directly"],
  },
  {
    id: "adaptive_leadership",
    tokens: ["adaptive leadership", "adaptive leadership model"],
  },
  {
    id: "executive_presence",
    tokens: ["executive presence", "leadership presence"],
  },
  {
    id: "inclusive_leadership",
    tokens: ["inclusive leadership", "juliet bourke inclusive leadership"],
  },
  {
    id: "servant_leadership",
    tokens: ["servant leadership", "greenleaf servant leadership"],
  },
];

function normalizeFrameworkPackText(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

/** @deprecated Use Supabase `framework_pack_catalog` table for text-based pack lookup. */
export function findFrameworkPackByText(value: string): FrameworkPackDefinition | null {
  const normalized = normalizeFrameworkPackText(value);
  if (!normalized) return null;

  const match = FRAMEWORK_PACK_TEXT_LOOKUP.find((candidate) =>
    candidate.tokens.some((token) => normalizeFrameworkPackText(token) === normalized),
  );

  return match ? getFrameworkPackById(match.id) : null;
}
